import { apiClient, publicApiClient } from './client';
import { getAccessToken, getRefreshToken, useAuthStore, type PendingEmailChange, type SessionTokens } from '../store/auth-store';
import type { StoredMarketingAttribution } from '../utils/marketing-attribution';

export type SuccessEnvelope<TData = unknown> = {
  success: true;
  data: TData;
  message?: string;
  meta?: Record<string, unknown> | null;
};

type ErrorEnvelopeError =
  | string
  | {
      message: string;
      code?: string;
      details?: unknown;
    };

export type ErrorEnvelope = {
  success: false;
  error: ErrorEnvelopeError;
  code?: string;
  requestId?: string;
  action?: string;
  username?: string;
  email?: string | null;
  session?: string;
  verificationSession?: VerificationSessionPayload | null;
  pendingEmailChange?: PendingEmailChange | null;
  challenge?: string;
  resendUrl?: string;
  details?: unknown;
};

type ApiError = Error & {
  status?: number;
  code?: string;
  action?: string;
  username?: string;
  email?: string | null;
  session?: string;
  verificationSession?: VerificationSessionPayload | null;
  pendingEmailChange?: PendingEmailChange | null;
  challenge?: string;
  resendUrl?: string;
  details?: unknown;
};

const createApiError = (fallbackMessage: string, status: number | undefined, envelope?: ErrorEnvelope) => {
  let message = fallbackMessage;
  let derivedCode: string | undefined;
  let derivedDetails: unknown;

  if (envelope?.error) {
    if (typeof envelope.error === 'string') {
      message = envelope.error;
    } else if (typeof envelope.error === 'object' && envelope.error !== null) {
      if (envelope.error.message) {
        message = String(envelope.error.message);
      }
      derivedCode = envelope.error.code ?? derivedCode;
      derivedDetails = envelope.error.details ?? derivedDetails;
    }
  }

  if (envelope?.code && !derivedCode) {
    derivedCode = envelope.code;
  }

  const error = new Error(message) as ApiError;
  if (typeof status === 'number') {
    error.status = status;
  }
  if (derivedCode) {
    error.code = derivedCode;
  } else if (envelope?.code) {
    error.code = envelope.code;
  }
  if (envelope?.action) {
    error.action = envelope.action;
  }
  if (envelope?.username) {
    error.username = envelope.username;
  }
  if (envelope?.email !== undefined) {
    error.email = envelope.email;
  }
  if (envelope?.session) {
    error.session = envelope.session;
  }
  if (envelope?.verificationSession) {
    error.verificationSession = envelope.verificationSession;
  }
  if (envelope?.pendingEmailChange) {
    error.pendingEmailChange = envelope.pendingEmailChange;
  }
  if (envelope?.challenge) {
    error.challenge = envelope.challenge;
  }
  if (envelope?.resendUrl) {
    error.resendUrl = envelope.resendUrl;
  }
  if (derivedDetails !== undefined) {
    error.details = derivedDetails;
  } else if (envelope?.details !== undefined) {
    error.details = envelope.details;
  }
  return error;
};

const unwrap = async <TData>(request: Promise<unknown>): Promise<TData> => {
  const result = await request;
  const payload = result as {
    data?: unknown;
    error?: Error;
    response?: Response;
  };

  if (payload.error) {
    const status = payload.response?.status;
    let envelope = payload.data as ErrorEnvelope | undefined;

    if (!envelope && payload.response) {
      try {
        const cloned = payload.response.clone();
        const json = await cloned.json();
        if (json && typeof json === 'object' && 'success' in json) {
          envelope = json as ErrorEnvelope;
        }
      } catch {
        // ignore parse failure, fall back to existing payload
      }
    }

    const rawMessage = typeof envelope?.error === 'string'
      ? envelope?.error
      : (envelope?.error && typeof envelope.error === 'object' && envelope.error !== null
        ? envelope.error.message
        : undefined);
    const message = rawMessage || payload.error.message || 'Unable to complete request';
    throw createApiError(message, status, envelope);
  }

  const envelope = payload.data as SuccessEnvelope<TData> | ErrorEnvelope | undefined;
  if (!envelope) {
    throw new Error('Empty response from server');
  }

  if ('success' in envelope && envelope.success === false) {
    const rawMessage = typeof envelope.error === 'string'
      ? envelope.error
      : (envelope.error && typeof envelope.error === 'object' && envelope.error !== null
        ? envelope.error.message
        : undefined);
    throw createApiError(rawMessage || 'Request failed', payload.response?.status, envelope);
  }

  if (!('data' in envelope)) {
    throw new Error('Unexpected response shape');
  }

  return (envelope as SuccessEnvelope<TData>).data;
};

export type LoginPayload = {
  username: string;
  password: string;
};

export type LoginSuccess = {
  token: string;
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
  deviceKey?: string;
  deviceGroupKey?: string;
  sessionId?: string;
  requiresPayment?: boolean;
  checkoutUrl?: string | null;
  user?: Record<string, unknown>;
  consentStatus?: Record<string, unknown> | null;
  requiresConsent?: boolean;
  consentUpdateUrl?: string | null;
  restrictedAccess?: boolean;
  pendingConsents?: string[];
  outdatedDocuments?: Array<Record<string, unknown>>;
};

export const login = async (payload: LoginPayload) =>
  unwrap<LoginSuccess>(publicApiClient.POST('/auth/login', { body: payload }));

export type SignupPayload = {
  username: string;
  email: string;
  password: string;
  timezone: string;
  acceptedTerms: boolean;
  acceptedPrivacy: boolean;
  acceptedLiability: boolean;
  acceptedAi: boolean;
  acceptedAdultAttestation: boolean;
  acceptedDataProcessing?: boolean;
  acceptedAup?: boolean;
  marketingOptIn?: boolean;
  tier?: 'starter' | 'creator' | 'pro' | 'business';
  billingCycle?: 'monthly' | 'annual';
  trial?: boolean;
  successUrl?: string;
  cancelUrl?: string;
  marketingAttribution?: StoredMarketingAttribution;
};

export type SignupSuccess =
  | ({
      message: string;
      verified: true;
    } & LoginSuccess)
  | {
      message: string;
      status: 'pending_verification';
      email: string;
      user: Record<string, unknown>;
    };

export const signup = async (payload: SignupPayload) =>
  unwrap<SignupSuccess>(publicApiClient.POST('/auth/signup', { body: payload }));

export type ForgotPasswordPayload = {
  identifier: string;
};

export type ForgotPasswordSuccess = {
  message: string;
};

export const forgotPassword = async (payload: ForgotPasswordPayload) =>
  unwrap<ForgotPasswordSuccess>(publicApiClient.POST('/auth/forgot-password', { body: payload }));

export type RecoverUsernamePayload = {
  email: string;
};

export type RecoverUsernameSuccess = {
  message: string;
};

export const recoverUsername = async (payload: RecoverUsernamePayload) =>
  unwrap<RecoverUsernameSuccess>(publicApiClient.POST('/auth/recover-username', { body: payload }));

export type ResetPasswordPayload = {
  identifier: string;
  code: string;
  password?: string;
  newPassword?: string;
};

export type ResetPasswordSuccess = {
  message: string;
};

export const resetPassword = async (payload: ResetPasswordPayload) =>
  unwrap<ResetPasswordSuccess>(publicApiClient.POST('/auth/reset-password', { body: payload }));

export type ChangePasswordPayload = {
  currentPassword: string;
  newPassword: string;
  confirmPassword?: string;
};

type ChangePasswordRequest = ChangePasswordPayload & { accessToken?: string };

export type ChangePasswordSuccess = {
  message: string;
};

export const changePassword = async (payload: ChangePasswordPayload) => {
  const state = useAuthStore.getState();
  let sessionTokens = state.tokens ?? null;
  const refreshToken = sessionTokens?.refreshToken ?? getRefreshToken();

  if (refreshToken) {
    try {
      const refreshed = await refreshSession({ refreshToken });
      sessionTokens = {
        accessToken: refreshed.accessToken,
        refreshToken: refreshed.refreshToken ?? refreshToken,
        idToken: refreshed.token ?? sessionTokens?.idToken,
        expiresAt: refreshed.expiresIn
          ? Date.now() + refreshed.expiresIn * 1000
          : sessionTokens?.expiresAt,
        deviceKey: refreshed.deviceKey ?? sessionTokens?.deviceKey ?? null,
        deviceGroupKey: refreshed.deviceGroupKey ?? sessionTokens?.deviceGroupKey ?? null,
      } satisfies SessionTokens;
      state.updateTokens(sessionTokens);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[auth] changePassword pre-refresh failed', error);
      }
    }
  }

  const accessToken = sessionTokens?.accessToken ?? getAccessToken();
  const body: ChangePasswordRequest = accessToken ? { ...payload, accessToken } : payload;

  try {
    return await unwrap<ChangePasswordSuccess>(apiClient.POST('/auth/change-password', { body }));
  } catch (error) {
    const apiError = error as ApiError;
    if (apiError?.code !== 'AUTH_TOKEN_INVALID') {
      throw error;
    }

    const refreshToken = state.tokens?.refreshToken ?? getRefreshToken();
    if (!refreshToken) {
      throw error;
    }

    try {
      const refreshed = await refreshSession({ refreshToken });
      const updatedTokens = {
        accessToken: refreshed.accessToken,
        refreshToken: refreshed.refreshToken ?? refreshToken,
        idToken: refreshed.token ?? state.tokens?.idToken,
        expiresAt: refreshed.expiresIn
          ? Date.now() + refreshed.expiresIn * 1000
          : state.tokens?.expiresAt,
        deviceKey: refreshed.deviceKey ?? state.tokens?.deviceKey ?? null,
        deviceGroupKey: refreshed.deviceGroupKey ?? state.tokens?.deviceGroupKey ?? null,
      } satisfies SessionTokens;

      state.updateTokens(updatedTokens);

      return await unwrap<ChangePasswordSuccess>(
        apiClient.POST('/auth/change-password', {
          body: { ...payload, accessToken: updatedTokens.accessToken } as ChangePasswordRequest,
        }),
      );
    } catch (refreshError) {
      if (import.meta.env.DEV) {
        console.warn('[auth] changePassword refresh failed', refreshError);
      }
      throw error;
    }
  }
};

export type VerificationSessionPayload = {
  refreshToken?: string;
  accessToken?: string;
  idToken?: string;
  expiresIn?: number;
  deviceKey?: string;
  deviceGroupKey?: string | null;
};

export type VerifyEmailPayload = {
  username: string;
  code: string;
  session?: VerificationSessionPayload | null;
};

export type VerifyEmailSuccess = {
  message: string;
};

export const verifyEmail = async (payload: VerifyEmailPayload) =>
  unwrap<VerifyEmailSuccess>(publicApiClient.POST('/auth/verify-email', { body: payload }));

export type ResendVerificationPayload = {
  username: string;
  session?: VerificationSessionPayload | null;
};

export type ResendVerificationSuccess = {
  message: string;
};

export const resendVerification = async (payload: ResendVerificationPayload) =>
  unwrap<ResendVerificationSuccess>(publicApiClient.POST('/auth/resend-verification', { body: payload }));

export type CompleteNewPasswordPayload = {
  username: string;
  session: string;
  newPassword: string;
  confirmPassword?: string;
};

export const completeNewPassword = async (payload: CompleteNewPasswordPayload) =>
  unwrap<LoginSuccess>(publicApiClient.POST('/auth/complete-new-password', { body: payload }));

export type RefreshPayload = {
  refreshToken: string;
};

export type RefreshSuccess = {
  token: string;
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
  deviceKey?: string | null;
  deviceGroupKey?: string | null;
};

export const refreshSession = async (payload: RefreshPayload) =>
  unwrap<RefreshSuccess>(publicApiClient.POST('/auth/refresh', { body: payload }));

export type LogoutSuccess = {
  message: string;
};

export const logout = async () => unwrap<LogoutSuccess>(apiClient.POST('/auth/logout'));
